import { stripAccents } from "./normalize";

/**
 * Detecção de intenção por padrões explícitos de início de frase.
 *
 * Os padrões são DADOS, não heurística escondida no meio do código: dá para ler
 * a lista, discutir com quem atende o suporte e testar cada linha.
 *
 * A intenção não escolhe o documento — o motor faz isso. Ela decide qual `kind`
 * é priorizado no desempate e o que o chat oferece depois da resposta.
 */

export type Intent = "what" | "how" | "why" | "limit" | "troubleshoot" | "unknown";

interface IntentPattern {
  intent: Intent;
  /** Casado contra a pergunta sem acento e em minúsculas. */
  pattern: RegExp;
  /** Por que este padrão existe. */
  note: string;
}

/**
 * Ordem importa: o primeiro que casar vence. Os padrões mais específicos vêm
 * antes — "não consigo" é problema, mesmo contendo "como" numa frase maior.
 */
export const INTENT_PATTERNS: IntentPattern[] = [
  {
    intent: "troubleshoot",
    pattern: /^(nao|n)\s+(consigo|consegui|funciona|carrega|aparece|abre|deixa|salva|vai)\b/,
    note: "A forma mais comum de relatar um problema começa por 'não consigo'.",
  },
  {
    intent: "troubleshoot",
    pattern: /\b(deu erro|deu problema|travou|bugou|sumiu|desapareceu|nao aparece|em branco)\b/,
    note: "Palavras de falha, em qualquer posição da frase.",
  },
  {
    intent: "limit",
    pattern: /^(posso|da pra|consigo|tem como|e possivel|sera que da)\b/,
    note: "Pergunta de permissão: a resposta útil é o limite, não o passo a passo.",
  },
  {
    intent: "limit",
    pattern: /\b(limite|maximo|quantos posso|quantas posso|irreversivel|desfazer|volta atras)\b/,
    note: "Fala explícita de teto ou de reversibilidade.",
  },
  {
    intent: "why",
    pattern: /^(por que|porque|pq|por q|porq)\b/,
    note: "Pergunta de motivo. Merece a regra E a razão dela, não o procedimento.",
  },
  {
    intent: "why",
    pattern: /\b(por que|porque|pq)\b.*\b(nao|bloqueou|recusou|exige|obriga)\b/,
    note: "Motivo de uma recusa do sistema, no meio da frase.",
  },
  {
    intent: "how",
    pattern: /^(como|onde|qual o caminho|de que jeito|quero|preciso|gostaria)\b/,
    note: "Pergunta de procedimento — a mais frequente de todas.",
  },
  {
    intent: "how",
    pattern: /^(passo a passo|me ensina|me mostra)\b/,
    note: "Pedido explícito de instrução.",
  },
  {
    intent: "what",
    pattern: /^(o que|oq|que e|que significa|significa|para que serve|pra que serve|qual a diferenca)\b/,
    note: "Pergunta de definição.",
  },
  {
    intent: "what",
    pattern: /\b(o que (e|significa|quer dizer))\b/,
    note: "Definição pedida no meio da frase.",
  },
];

/** Como cada intenção prioriza os `kind` dos documentos, em ordem de preferência. */
export const INTENT_KIND_PRIORITY: Record<Intent, string[]> = {
  what: ["what", "rule", "how"],
  how: ["how", "rule", "what"],
  // Uma pergunta de motivo prefere a razão; na falta dela, a regra que o sistema aplica.
  why: ["why", "rule", "limit", "what"],
  limit: ["limit", "rule", "why"],
  troubleshoot: ["troubleshoot", "rule", "limit", "how"],
  unknown: [],
};

export function detectIntent(pergunta: string): Intent {
  const limpo = stripAccents(pergunta.toLowerCase()).replace(/[^\w\s]/g, " ").replace(/\s+/g, " ").trim();
  for (const { intent, pattern } of INTENT_PATTERNS) {
    if (pattern.test(limpo)) return intent;
  }
  return "unknown";
}

/**
 * Pequeno ajuste de ordem dentro do topo do ranking, conforme a intenção.
 *
 * Deliberadamente suave e limitado aos primeiros colocados: a intenção informa o
 * desempate, não substitui a recuperação. Reordenar a lista inteira faria uma
 * heurística de superfície mandar num motor que foi calibrado por medição.
 */
export function reorderByIntent<T extends { doc: { kind: string }; score: number }>(
  ranked: T[],
  intent: Intent,
  janela = 4,
  /**
   * Só entram no desempate os documentos a menos de 15% do primeiro colocado.
   *
   * Sem essa margem, a intenção passava por cima da recuperação: "como excluo um
   * imóvel" é `how`, mas a seção certa é do tipo `limit` — e ela perdia o
   * primeiro lugar, que tinha conquistado por pontuação, para um documento `how`
   * pior colocado. A intenção desempata; ela não decide.
   */
  margem = 0.15,
): T[] {
  const prioridade = INTENT_KIND_PRIORITY[intent];
  if (!prioridade.length || ranked.length < 2) return ranked;

  const melhor = ranked[0].score;
  const limite = Math.min(
    janela,
    ranked.findIndex((r) => r.score < melhor * (1 - margem)) === -1
      ? ranked.length
      : ranked.findIndex((r) => r.score < melhor * (1 - margem)),
  );
  if (limite < 2) return ranked;

  const topo = ranked.slice(0, limite);
  const resto = ranked.slice(limite);

  const posicao = (kind: string) => {
    const i = prioridade.indexOf(kind);
    return i === -1 ? prioridade.length : i;
  };

  const reordenado = [...topo].sort((a, b) => {
    const da = posicao(a.doc.kind);
    const db = posicao(b.doc.kind);
    if (da !== db) return da - db;
    return b.score - a.score;
  });

  return [...reordenado, ...resto];
}
